/**
 * Shared Jalali (Shamsi) date utilities
 * All functions work with ISO strings (YYYY-MM-DD) internally
 * and display Jalali equivalents using Intl.DateTimeFormat
 */
import React, { useState } from "react";
import { Popover, Button, Select, Space } from "antd";
import { faNum } from "./faNumberFormat";

export const JALALI_MONTHS = [
  "فروردین","اردیبهشت","خرداد","تیر","مرداد","شهریور",
  "مهر","آبان","آذر","دی","بهمن","اسفند"
];

// ===== تبدیلِ دقیقِ شمسی↔میلادی (الگوریتمِ jalaali-js) =====
// نکتهٔ مهم: jalaali-js از تقسیمِ بریده‌به‌سمتِ صفر استفاده می‌کند (نه Math.floor)
const _div = (a, b) => Math.trunc(a / b);
const _mod = (a, b) => a - Math.trunc(a / b) * b;
const _BREAKS = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210,
  1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
function _jalCal(jy) {
  let bl = _BREAKS.length, gy = jy + 621, leapJ = -14, jp = _BREAKS[0];
  let jm, jump = 0, leap, leapG, march, n, i;
  for (i = 1; i < bl; i += 1) {
    jm = _BREAKS[i]; jump = jm - jp;
    if (jy < jm) break;
    leapJ = leapJ + _div(jump, 33) * 8 + _div(_mod(jump, 33), 4);
    jp = jm;
  }
  n = jy - jp;
  leapJ = leapJ + _div(n, 33) * 8 + _div(_mod(n, 33) + 3, 4);
  if (_mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
  leapG = _div(gy, 4) - _div((_div(gy, 100) + 1) * 3, 4) - 150;
  march = 20 + leapJ - leapG;
  if (jump - n < 6) n = n - jump + _div(jump + 4, 33) * 33;
  leap = _mod(_mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}
function _g2d(gy, gm, gd) {
  let d = _div((gy + _div(gm - 8, 6) + 100100) * 1461, 4)
    + _div(153 * _mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  d = d - _div(_div(gy + 100100 + _div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}
function _d2g(jdn) {
  let j = 4 * jdn + 139361631;
  j = j + _div(_div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = _div(_mod(j, 1461), 4) * 5 + 308;
  const gd = _div(_mod(i, 153), 5) + 1;
  const gm = _mod(_div(i, 153), 12) + 1;
  const gy = _div(j, 1461) - 100100 + _div(8 - gm, 6);
  return { gy, gm, gd };
}
function _j2d(jy, jm, jd) {
  const r = _jalCal(jy);
  return _g2d(r.gy, 3, r.march) + (jm - 1) * 31 - _div(jm, 7) * (jm - 7) + jd - 1;
}
function _d2j(jdn) {
  const gy = _d2g(jdn).gy; let jy = gy - 621;
  const r = _jalCal(jy); const jdn1f = _g2d(gy, 3, r.march);
  let jd, jm, k = jdn - jdn1f;
  if (k >= 0) {
    if (k <= 185) { jm = 1 + _div(k, 31); jd = _mod(k, 31) + 1; return { jy, jm, jd }; }
    k -= 186;
  } else { jy -= 1; k += 179; if (r.leap === 1) k += 1; }
  jm = 7 + _div(k, 30); jd = _mod(k, 30) + 1;
  return { jy, jm, jd };
}
const _isLeapJalaali = (jy) => _jalCal(jy).leap === 0;
export function jalaaliMonthLength(jy, jm) {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return _isLeapJalaali(jy) ? 30 : 29;
}
/** {jy,jm,jd} → ISO میلادی */
export function jalaaliToISO(jy, jm, jd) {
  const g = _d2g(_j2d(jy, jm, jd));
  return `${g.gy}-${String(g.gm).padStart(2, "0")}-${String(g.gd).padStart(2, "0")}`;
}
/** ISO میلادی → {jy,jm,jd} */
export function isoToJalaali(iso) {
  if (!iso) return null;
  const [gy, gm, gd] = iso.split("-").map(Number);
  return _d2j(_g2d(gy, gm, gd));
}

export const JALALI_YEARS = [1402, 1403, 1404, 1405, 1406];

/** Convert ISO date string to Jalali display string */
export function toJalali(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric", month: "2-digit", day: "2-digit", calendar: "persian"
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** زمانِ سرور (مثلِ «2026-09-27 10:15:00.123456+03:30») → «۱۴۰۵/۰۷/۰۵ ۱۰:۱۵» به وقتِ تهران */
export function toJalaliDateTime(ts) {
  if (!ts) return "";
  // Safari «فاصله» و میکروثانیه را نمی‌خواند → ISOِ استاندارد
  const iso = String(ts).replace(" ", "T").replace(/(\.\d{3})\d+/, "$1");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(ts);
  try {
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      hour12: false, calendar: "persian", timeZone: "Asia/Tehran",
    }).format(d).replace(/[،,]\s*/, " ساعت ");
  } catch {
    return String(ts);
  }
}

/** Get today's ISO date string */
export function todayISO() {
  return new Date().toISOString().split("T")[0];
}

/** Convert Persian/Arabic-Indic digits to ASCII digits */
function toLatinDigits(str) {
  return String(str).replace(/[۰-۹]/g, d => d.charCodeAt(0) - 0x06F0)
                    .replace(/[٠-٩]/g, d => d.charCodeAt(0) - 0x0660);
}

/** Get current Jalali year and month */
export function currentJalali() {
  const parts = new Intl.DateTimeFormat("fa-IR", {
    year: "numeric", month: "numeric", calendar: "persian"
  }).formatToParts(new Date());
  const year = parseInt(toLatinDigits(parts.find(p => p.type === "year")?.value || "1404"));
  const month = parseInt(toLatinDigits(parts.find(p => p.type === "month")?.value || "1"));
  return { year, month };
}

/** Format number with Farsi locale */
export function fmtNum(n) {
  if (!n && n !== 0) return "—";
  return faNum(Number(n));
}

/**
 * formatDurationFa — دقیقه را به «ساعت و دقیقه» فارسی تبدیل می‌کند.
 * مثال‌ها: 75→«۱ ساعت و ۱۵ دقیقه»، 20→«۲۰ دقیقه»، 420→«۷ ساعت»، 600→«۱۰ ساعت»
 */
export function formatDurationFa(minutes) {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return "—";
  const m = Math.round(Number(minutes));
  const fa = (x) => faNum(Number(x));
  if (m === 0) return "۰ دقیقه";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${fa(h)} ساعت و ${fa(mm)} دقیقه`;
  if (h) return `${fa(h)} ساعت`;
  return `${fa(mm)} دقیقه`;
}

/**
 * JalaliDateInput — wrapper around <input type="date"> that shows Jalali date alongside
 * Usage: <JalaliDateInput value={isoStr} onChange={setIsoStr} />
 */
const _WEEKDAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

// تقویمِ گریدِ کاملاً شمسی (بدون هیچ میلادی)
function JalaliCalendarGrid({ value, onPick }) {
  const cj = currentJalali();
  const sel = value ? isoToJalaali(value) : null;
  const [view, setView] = useState({ jy: sel ? sel.jy : cj.year, jm: sel ? sel.jm : cj.month });
  const len = jalaaliMonthLength(view.jy, view.jm);
  const firstISO = jalaaliToISO(view.jy, view.jm, 1);
  const jsDow = new Date(firstISO + "T00:00:00").getDay();   // 0=یکشنبه..6=شنبه
  const offset = (jsDow + 1) % 7;                             // شنبه = ستون ۰
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= len; d++) cells.push(d);
  // 🐞 `faNum(1405)` جداکنندهٔ هزارگان می‌گذارد و سال «۱٬۴۰۵» دیده می‌شد.
  // سال و روزِ تقویم عددِ شمارشی نیستند، فقط باید ارقامشان فارسی شود.
  const fa = (n) => String(n).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
  const move = (delta) => setView((v) => {
    let m = v.jm + delta, y = v.jy;
    if (m < 1) { m = 12; y -= 1; } if (m > 12) { m = 1; y += 1; }
    return { jy: y, jm: m };
  });
  const todayJ = currentJalali();
  return (
    <div style={{ width: 272, direction: "rtl" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 4 }}>
        <Space size={2}>
          <Button size="small" title="سال قبل" onClick={() => setView((v) => ({ ...v, jy: v.jy - 1 }))}>«</Button>
          <Button size="small" onClick={() => move(-1)}>‹</Button>
        </Space>
        <Space>
          <Select size="small" value={view.jm} style={{ width: 96 }}
            onChange={(m) => setView((v) => ({ ...v, jm: m }))}
            options={JALALI_MONTHS.map((mn, i) => ({ value: i + 1, label: mn }))} />
          {/* سال: از ۱۳۰۰ تا دو سال بعد — تاریخِ تولد و استخدامِ قدیمی هم باید ثبت شود
              (قبلاً فقط ±۵ سال بود و «تا ۱۴۰۰» بسته بود). با تایپِ ارقامِ فارسی یا لاتین جست‌وجو می‌شود. */}
          <Select size="small" value={view.jy} style={{ width: 84 }} showSearch
            onChange={(y) => setView((v) => ({ ...v, jy: y }))}
            filterOption={(input, opt) => String(opt?.value ?? "").startsWith(toLatinDigits(input || ""))}
            popupMatchSelectWidth={false} listHeight={220}
            options={Array.from({ length: todayJ.year + 2 - 1300 + 1 }, (_, i) => todayJ.year + 2 - i)
              .map((y) => ({ value: y, label: fa(y) }))} />
        </Space>
        <Space size={2}>
          <Button size="small" onClick={() => move(1)}>›</Button>
          <Button size="small" title="سال بعد" onClick={() => setView((v) => ({ ...v, jy: v.jy + 1 }))}>»</Button>
        </Space>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, textAlign: "center" }}>
        {_WEEKDAYS.map((w) => (
          <div key={w} style={{ fontSize: 11, color: "var(--text-mute, #999)", padding: 2 }}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const isSel = sel && sel.jy === view.jy && sel.jm === view.jm && sel.jd === d;
          const isToday = todayJ.year === view.jy && todayJ.month === view.jm
            && isoToJalaali(todayISO()).jd === d;
          return (
            <div key={i} onClick={() => onPick(jalaaliToISO(view.jy, view.jm, d))}
              style={{
                cursor: "pointer", padding: "5px 0", borderRadius: 6, fontSize: 13,
                background: isSel ? "var(--brand, #1F4E79)" : "transparent",
                color: isSel ? "#fff" : "var(--text, #333)",
                border: isToday && !isSel
                  ? "1px solid var(--brand, #1F4E79)" : "1px solid transparent",
              }}>
              {fa(d)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ورودیِ تاریخِ کاملاً شمسی — تقویمِ شمسی، بدون هیچ تاریخِ میلادی در UI
export function JalaliDateInput({ value, onChange, style, id, zIndex = 1100 }) {
  const [open, setOpen] = useState(false);
  return (
    /* zIndex بالاتر از دراپ‌داونِ فیلترِ جدول (۱۰۵۰).
       پیش‌فرضِ Popover در antd برابرِ ۱۰۳۰ است، پس وقتی این ورودی داخلِ فیلترِ
       ستون باز می‌شد، تقویم **زیرِ** پنلِ فیلتر می‌رفت و ورودی‌ها و دکمه‌های
       پنل روی تقویم می‌افتادند — همان «رفته تو هم». */
    /* placement="bottom" (نه bottomRight): این ورودی گاهی داخلِ جدولی است که
       افقی اسکرول می‌شود؛ لنگرِ گوشه‌ای در RTL تقویم را بیرونِ کادرِ صفحه
       می‌انداخت و صفحه به‌زور افقی اسکرول می‌شد. */
    <Popover open={open} onOpenChange={setOpen} trigger="click" placement="bottom"
      autoAdjustOverflow zIndex={zIndex} arrow={false}
      content={
        <div>
          <JalaliCalendarGrid value={value} onPick={(iso) => { onChange(iso); setOpen(false); }} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <Button size="small" type="link" onClick={() => { onChange(todayISO()); setOpen(false); }}>امروز</Button>
            {value && <Button size="small" type="link" danger onClick={() => { onChange(null); setOpen(false); }}>پاک کردن</Button>}
          </div>
        </div>
      }>
      <span id={id} style={{
        display: "inline-flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        padding: "6px 10px", borderRadius: 8,
        // رنگ‌های ثابت در حالتِ تیره یک جعبهٔ سفیدِ ناجور می‌ساختند
        border: "1px solid var(--border-strong, #d9d9d9)",
        background: "var(--surface, #fff)", color: "var(--text, inherit)",
        fontSize: 13, minWidth: 150, cursor: "pointer", ...style,
      }}>
        <span style={{ color: value ? "var(--brand, #1F4E79)" : "var(--text-mute, #999)",
          fontWeight: value ? 600 : 400 }}>
          {value ? toJalali(value) : "انتخاب تاریخ شمسی"}
        </span>
        <span style={{ opacity: 0.55 }}>📅</span>
      </span>
    </Popover>
  );
}
